/** @format */

import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Button,
	Container,
	CircularProgress,
	Alert,
	Divider,
	List,
	ListItem,
	ListItemText,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Paper,
	Grid,
	Avatar,
	Snackbar,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import PaymentIcon from "@mui/icons-material/Payment";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { colors } from "../../utility/types/constant";
import { AppDispatch, RootState } from "../../redux/store/store";
import {
	clearOutstandingDuesState,
	fetchMySuspensionStatus,
	OutstandingDues,
	payAllOutstandingDues,
	SuspensionCircleDetail,
	SuspensionStatus,
} from "../../redux/actions/outstandingDuesAction";
import { NotificationModal } from "../allComponents/AllComponents";
import {
	calculateTotalCharge,
	formatCurrency as formatCurrencyWithFee,
} from "../../utility/stripeFeeCalculator";
import { formatCurrency, ZERO_DECIMAL_CURRENCIES } from "../../utility/currencyUtils";

const AccountBanned = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch<AppDispatch>();
	const stripe = useStripe();
	const elements = useElements();

	// Local state for modal and payment
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [paymentConfirmed, setPaymentConfirmed] = useState(false);
	const [localPaymentProcessing, setLocalPaymentProcessing] = useState(false);
	const [localPaymentError, setLocalPaymentError] = useState<string | null>(
		null,
	);
	const [openSuccessModal, setOpenSuccessModal] = useState(false);
	const [alertState, setAlertState] = useState<{
		open: boolean;
		type: "success" | "error";
		message: string;
	}>({ open: false, type: "success", message: "" });

	// Redux state - use direct state access with proper typing
	const suspensionStatus = useSelector(
		(state: RootState) => state.outstandingDues?.suspensionStatus,
	) as SuspensionStatus | null;
	const loading = useSelector(
		(state: RootState) => state.outstandingDues?.loading,
	) as boolean;
	const error = useSelector(
		(state: RootState) => state.outstandingDues?.error,
	) as string | null;
	const paymentProcessing = useSelector(
		(state: RootState) => state.outstandingDues?.paymentProcessing,
	) as boolean;
	const paymentSuccess = useSelector(
		(state: RootState) => state.outstandingDues?.paymentSuccess,
	) as boolean;

	// Extract ban details from navigation state (passed from 403 response)
	// These are the primary source since the API call may fail for banned users
	const locationState = location.state || {};
	const bannedAt = locationState.bannedAt || suspensionStatus?.bannedAt;
	const reason = locationState.reason || suspensionStatus?.banReason;
	const banType = locationState.banType || suspensionStatus?.banType;
	const canSelfResolve =
		locationState.canSelfResolve ||
		suspensionStatus?.canSelfResolve ||
		banType === "missed_contributions";

	// Build outstanding dues from location state if API call failed
	// The 403 response now includes suspensionDetails
	const locationSuspensionDetails = locationState.suspensionDetails;
	const outstandingDuesFromLocation: OutstandingDues | undefined =
		locationSuspensionDetails ?
			{
				userId: "",
				email: "",
				firstName: "",
				lastName: "",
				missedContributionsCents:
					locationSuspensionDetails.missedContributionsCents || 0,
				lateFeesCents: locationSuspensionDetails.lateFeesCents || 0,
				totalDueCents: locationSuspensionDetails.totalDueCents || 0,
				affectedCircles: locationSuspensionDetails.affectedCircles || [],
				hasOutstandingDues: (locationSuspensionDetails.totalDueCents || 0) > 0,
				eligibleForAutoSuspension: true,
				payoutProcessedWithoutPayment: true,
			}
			: undefined;

	// Build outstanding dues from API's suspensionDetails if outstandingDues shows 0
	// This can happen when contributions were marked paid but suspension wasn't lifted
	const apiSuspensionDetails = suspensionStatus?.suspensionDetails;
	const outstandingDuesFromApiSuspensionDetails: OutstandingDues | undefined =
		apiSuspensionDetails && apiSuspensionDetails.totalDueCents > 0 ?
			{
				userId: suspensionStatus?.outstandingDues?.userId || "",
				email: suspensionStatus?.outstandingDues?.email || "",
				firstName: suspensionStatus?.outstandingDues?.firstName || "",
				lastName: suspensionStatus?.outstandingDues?.lastName || "",
				missedContributionsCents:
					apiSuspensionDetails.missedContributionsCents || 0,
				lateFeesCents: apiSuspensionDetails.lateFeesCents || 0,
				totalDueCents: apiSuspensionDetails.totalDueCents || 0,
				affectedCircles: apiSuspensionDetails.affectedCircles || [],
				hasOutstandingDues: true,
				eligibleForAutoSuspension: true,
				payoutProcessedWithoutPayment: true,
			}
			: undefined;

	// Use API outstandingDues if it has dues, otherwise fall back to suspensionDetails, then location state
	const outstandingDues: OutstandingDues | undefined =
		(suspensionStatus?.outstandingDues?.hasOutstandingDues ?
			suspensionStatus.outstandingDues
			: null) ||
		outstandingDuesFromApiSuspensionDetails ||
		outstandingDuesFromLocation;

	// Calculate fee breakdown for the payment
	const feeBreakdown = useMemo(() => {
		if (!outstandingDues) return null;
		const currency = outstandingDues.currency ?? "usd";
		// totalDueCents is in the smallest unit — convert to display amount before fee calc
		const amountInDollars =
			ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase()) ?
				outstandingDues.totalDueCents
				: outstandingDues.totalDueCents / 100;
		return calculateTotalCharge(amountInDollars, currency);
	}, [outstandingDues]);

	// Fetch suspension status on mount
	// Now that suspended users can authenticate, this API call should succeed
	useEffect(() => {
		// Always fetch fresh data from API - now that the user has valid tokens
		dispatch(fetchMySuspensionStatus());

		return () => {
			dispatch(clearOutstandingDuesState());
		};
	}, [dispatch, locationSuspensionDetails]);

	// Handle successful payment - redirect to user profile
	useEffect(() => {
		if (paymentSuccess) {
			setPaymentConfirmed(true);
			setOpenSuccessModal(true);
			// Redirect to login page after a short delay so user can re-login
			setTimeout(() => {
				setOpenSuccessModal(false);
				navigate("/circle/sign-in");
			}, 3000);
		}
	}, [paymentSuccess, navigate]);

	const formatDate = (dateString?: string) => {
		if (!dateString) return "N/A";
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const formatCurrencyFromCents = (cents: number) => {
		return formatCurrency(cents / 100, "usd");
	};

	const handlePayOutstandingDues = () => {
		setLocalPaymentError(null);
		setShowPaymentModal(true);
	};

	const handleConfirmPayment = async () => {
		if (!stripe || !elements) {
			setLocalPaymentError("Payment system not ready. Please try again.");
			return;
		}

		setLocalPaymentProcessing(true);
		setLocalPaymentError(null);

		const cardElement = elements.getElement(CardElement);

		if (!cardElement) {
			setLocalPaymentError(
				"Card details not found. Please refresh and try again.",
			);
			setLocalPaymentProcessing(false);
			return;
		}

		try {
			// Create payment method from card
			const { error: pmError, paymentMethod } =
				await stripe.createPaymentMethod({
					type: "card",
					card: cardElement,
				});

			if (pmError || !paymentMethod) {
				setLocalPaymentError(
					pmError?.message || "Failed to process card. Please try again.",
				);
				setLocalPaymentProcessing(false);
				return;
			}

			// Dispatch payment with the payment method ID
			const result = await dispatch(
				payAllOutstandingDues(paymentMethod.id),
			).unwrap();

			if (result.success) {
				setShowPaymentModal(false);
				// Success handling is done in the useEffect above
			} else {
				setLocalPaymentError(
					result.message || "Payment failed. Please try again.",
				);
			}
		} catch (err: any) {
			setLocalPaymentError(
				err?.message || err || "Payment failed. Please try again.",
			);
			setAlertState({
				open: true,
				type: "error",
				message: err?.message || err || "Payment failed. Please try again.",
			});
		} finally {
			setLocalPaymentProcessing(false);
		}
	};

	const handleCloseModal = () => {
		setShowPaymentModal(false);
		setLocalPaymentError(null);
	};

	const handleCloseAlert = () => {
		setAlertState({ ...alertState, open: false });
	};

	// Only show loading if we're fetching AND don't have location state data
	if (loading && !locationSuspensionDetails && !bannedAt) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: colors.primary,
				}}>
				<CircularProgress sx={{ color: "white" }} />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: colors.primary,
				py: 4,
			}}>
			<Container maxWidth="sm">
				<Card
					elevation={10}
					sx={{
						borderRadius: 4,
						overflow: "hidden",
					}}>
					<Box
						sx={{
							background:
								canSelfResolve ?
									"linear-gradient(135deg, #f5af19 0%, #f12711 100%)"
									: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
							py: 4,
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							color: "white",
						}}>
						{canSelfResolve ?
							<WarningIcon sx={{ fontSize: 80, mb: 2 }} />
							: <BlockIcon sx={{ fontSize: 80, mb: 2 }} />}
						<Typography variant="h4" component="h1" fontWeight="bold">
							{canSelfResolve ?
								"Account Suspended - Payment Required"
								: "Account Suspended"}
						</Typography>
					</Box>

					<CardContent sx={{ p: 4 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 3 }}>
								{error}
							</Alert>
						)}

						{paymentConfirmed && (
							<Alert
								severity="success"
								sx={{ mb: 3 }}
								icon={<CheckCircleIcon />}>
								Payment successful! Your account has been reactivated.
								Redirecting to your profile...
							</Alert>
						)}

						<Typography
							variant="body1"
							color="text.secondary"
							sx={{ mb: 3, textAlign: "center" }}>
							{canSelfResolve ?
								"Your account has been suspended due to outstanding contributions and late fees. You can resolve this by paying your dues below."
								: "Your account has been suspended and you are unable to access the platform at this time."
							}
						</Typography>

						{bannedAt && (
							<Box sx={{ mb: 3, p: 2, bgcolor: "grey.100", borderRadius: 2 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Suspended On:
								</Typography>
								<Typography variant="body1" fontWeight="medium">
									{formatDate(bannedAt)}
								</Typography>
							</Box>
						)}

						{reason && (
							<Box sx={{ mb: 3, p: 2, bgcolor: "grey.100", borderRadius: 2 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Reason:
								</Typography>
								<Typography variant="body1" fontWeight="medium">
									{reason}
								</Typography>
							</Box>
						)}

						{/* Outstanding Dues Section - Only for self-resolvable suspensions */}
						{canSelfResolve && outstandingDues?.hasOutstandingDues && (
							<Box sx={{ mb: 3 }}>
								<Divider sx={{ my: 2 }} />
								<Typography
									variant="h6"
									gutterBottom
									sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<PaymentIcon /> Outstanding Dues
								</Typography>

								<Box
									sx={{
										p: 2,
										bgcolor: "error.lighter",
										borderRadius: 2,
										mb: 2,
									}}>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="error.main"
										textAlign="center">
										Total Due: {formatCurrencyFromCents(outstandingDues.totalDueCents)}
									</Typography>
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											gap: 2,
											mt: 1,
										}}>
										<Chip
											label={`Contributions: ${formatCurrencyFromCents(outstandingDues.missedContributionsCents)}`}
											color="warning"
											size="small"
										/>
										<Chip
											label={`Late Fees: ${formatCurrencyFromCents(outstandingDues.lateFeesCents)}`}
											color="error"
											size="small"
										/>
									</Box>
								</Box>

								<Typography
									variant="subtitle2"
									color="text.secondary"
									sx={{ mb: 1 }}>
									Affected Circles:
								</Typography>
								<List dense>
									{outstandingDues.affectedCircles.map(
										(circle: SuspensionCircleDetail) => (
											<ListItem
												key={circle.circleId}
												sx={{ bgcolor: "grey.50", borderRadius: 1, mb: 1 }}>
												<ListItemText
													primary={circle.circleName}
													secondary={
														<>
															<Typography
																variant="body2"
																component="span"
																color="text.secondary">
																{circle.missedDates.length} missed date(s):{" "}
																{circle.missedDates.slice(0, 3).join(", ")}
																{circle.missedDates.length > 3 &&
																	` +${circle.missedDates.length - 3} more`}
															</Typography>
															<br />
															<Typography
																variant="body2"
																component="span"
																color="warning.main">
																Contributions:{" "}
																{formatCurrencyFromCents(circle.missedAmountCents)}
															</Typography>
															{circle.lateFeesCents > 0 && (
																<>
																	{" | "}
																	<Typography
																		variant="body2"
																		component="span"
																		color="error.main">
																		Late Fees:{" "}
																		{formatCurrencyFromCents(circle.lateFeesCents)}
																	</Typography>
																</>
															)}
														</>
													}
												/>
											</ListItem>
										),
									)}
								</List>

								<Button
									variant="contained"
									fullWidth
									size="large"
									startIcon={
										paymentProcessing ?
											<CircularProgress size={20} color="inherit" />
											: <PaymentIcon />
									}
									disabled={paymentProcessing}
									sx={{
										py: 1.5,
										mt: 2,
										backgroundColor: colors.mainSecondary,
										"&:hover": {
											backgroundColor: colors.mainSecondary,
											opacity: 0.9,
										},
									}}
									onClick={handlePayOutstandingDues}>
									{paymentProcessing ?
										"Processing Payment..."
										: `Pay ${formatCurrencyFromCents(outstandingDues.totalDueCents)} to Reactivate Account`
									}
								</Button>
							</Box>
						)}

						{!canSelfResolve && (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 4, textAlign: "center" }}>
								If you believe this suspension is a mistake or would like to
								appeal this decision, please contact our support team.
							</Typography>
						)}

						<Box
							sx={{
								display: "flex",
								gap: 2,
								flexDirection: { xs: "column", sm: "row" },
								mt: 2,
							}}>
							<Button
								variant={canSelfResolve ? "outlined" : "contained"}
								fullWidth
								sx={{
									py: 1.5,
									...(canSelfResolve ?
										{
											borderColor: colors.mainSecondary,
											color: colors.mainSecondary,
											"&:hover": {
												borderColor: colors.mainSecondary,
												backgroundColor: "rgba(0, 0, 0, 0.04)",
											},
										}
										: {
											backgroundColor: colors.mainSecondary,
											"&:hover": {
												backgroundColor: colors.mainSecondary,
												opacity: 0.9,
											},
										}),
								}}
								href="mailto:support@forus.one?subject=Account%20Suspension%20Appeal">
								Contact Support
							</Button>
							<Button
								variant="outlined"
								fullWidth
								sx={{
									py: 1.5,
									borderColor: colors.mainSecondary,
									color: colors.mainSecondary,
									"&:hover": {
										borderColor: colors.mainSecondary,
										backgroundColor: "rgba(0, 0, 0, 0.04)",
									},
								}}
								onClick={() => navigate("/")}>
								Return to Home
							</Button>
						</Box>
					</CardContent>
				</Card>
			</Container>

			{/* Payment Confirmation Modal */}
			<Dialog
				open={showPaymentModal}
				onClose={handleCloseModal}
				maxWidth="md"
				fullWidth>
				<DialogTitle>Pay Outstanding Dues</DialogTitle>
				<DialogContent>
					{localPaymentError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{localPaymentError}
						</Alert>
					)}

					<Alert severity="warning" sx={{ mb: 2 }}>
						You are paying outstanding dues to reactivate your suspended
						account.
					</Alert>

					{outstandingDues && feeBreakdown && (
						<Box component="form" sx={{ mt: 2 }}>
							{/* Payment Summary Section */}
							<Paper
								elevation={0}
								sx={{
									p: 2,
									mb: 3,
									backgroundColor: "#f8fafc",
									border: "1px solid #e2e8f0",
									borderRadius: 2,
								}}>
								<Typography
									variant="subtitle1"
									sx={{ fontWeight: 600, mb: 2, color: "#334155" }}>
									Payment Summary
								</Typography>

								{/* Missed Contributions */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										mb: 1,
									}}>
									<Typography variant="body2" sx={{ color: "#64748b" }}>
										Missed Contributions (
										{outstandingDues.affectedCircles.length} circle
										{outstandingDues.affectedCircles.length > 1 ? "s" : ""})
									</Typography>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{formatCurrencyFromCents(outstandingDues.missedContributionsCents)}
									</Typography>
								</Box>

								{/* Late Fees */}
								{outstandingDues.lateFeesCents > 0 && (
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											mb: 1,
											backgroundColor: "#fef3c7",
											borderRadius: 1,
											px: 1,
											py: 0.5,
											mx: -1,
										}}>
										<Box
											sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<WarningAmberIcon
												sx={{ fontSize: 16, color: "#d97706" }}
											/>
											<Typography variant="body2" sx={{ color: "#92400e" }}>
												Late Fees (2.5%)
											</Typography>
											<InfoOutlinedIcon
												sx={{
													fontSize: 14,
													color: "#d97706",
													cursor: "help",
												}}
												titleAccess="A 2.5% late fee applies to contributions paid after their scheduled date."
											/>
										</Box>
										<Typography
											variant="body2"
											sx={{ fontWeight: 500, color: "#92400e" }}>
											+{formatCurrencyFromCents(outstandingDues.lateFeesCents)}
										</Typography>
									</Box>
								)}

								{/* Processing Fee */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										mb: 1,
									}}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Typography variant="body2" sx={{ color: "#64748b" }}>
											Processing Fee (2.9% + $0.30)
										</Typography>
										<InfoOutlinedIcon
											sx={{
												fontSize: 14,
												color: "#94a3b8",
												cursor: "help",
											}}
											titleAccess="This fee covers card payment processing costs."
										/>
									</Box>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{formatCurrencyWithFee(feeBreakdown.stripeFee)}
									</Typography>
								</Box>

								<Divider sx={{ my: 1.5 }} />

								{/* Total */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
									}}>
									<Typography
										variant="body1"
										sx={{ fontWeight: 600, color: "#1e293b" }}>
										Total to be charged
									</Typography>
									<Typography
										variant="body1"
										sx={{
											fontWeight: 700,
											color: "#dc2626",
										}}>
										{formatCurrencyWithFee(feeBreakdown.totalCharge)}
									</Typography>
								</Box>
							</Paper>

							{/* Transparency Note */}
							<Alert
								severity="info"
								icon={<InfoOutlinedIcon fontSize="small" />}
								sx={{
									mb: 3,
									backgroundColor: "#eff6ff",
									"& .MuiAlert-icon": { color: "#3b82f6" },
								}}>
								<Typography variant="body2" sx={{ color: "#1e40af" }}>
									<strong>
										100% of your {formatCurrencyFromCents(outstandingDues.totalDueCents)}{" "}
										dues
									</strong>{" "}
									goes directly to cover your missed contributions and late
									fees. The processing fee covers card payment costs only.
								</Typography>
							</Alert>

							{/* Card Element */}
							<Typography variant="subtitle2" sx={{ mb: 1, color: "#334155" }}>
								Enter Card Details
							</Typography>
							<Box
								sx={{
									mb: 3,
									p: 2,
									border: "1px solid #e2e8f0",
									borderRadius: 1,
									backgroundColor: "#fff",
								}}>
								<CardElement
									options={{
										style: {
											base: {
												fontSize: "16px",
												color: "#424770",
												"::placeholder": { color: "#aab7c4" },
											},
											invalid: { color: "#9e2146" },
										},
									}}
								/>
							</Box>

							{/* Affected Circles List */}
							<Typography variant="subtitle2" sx={{ mb: 1, color: "#334155" }}>
								Affected Circles
							</Typography>
							<List dense sx={{ bgcolor: "grey.50", borderRadius: 1, mb: 2 }}>
								{outstandingDues.affectedCircles.map(
									(circle: SuspensionCircleDetail) => (
										<ListItem key={circle.circleId}>
											<ListItemText
												primary={circle.circleName}
												secondary={
													<>
														<Typography
															variant="body2"
															component="span"
															color="text.secondary">
															{circle.missedDates.length} missed date(s)
														</Typography>
														{" • "}
														<Typography
															variant="body2"
															component="span"
															color="warning.main">
															{formatCurrencyFromCents(circle.missedAmountCents)}
														</Typography>
														{circle.lateFeesCents > 0 && (
															<>
																{" + "}
																<Typography
																	variant="body2"
																	component="span"
																	color="error.main">
																	{formatCurrencyFromCents(circle.lateFeesCents)} fees
																</Typography>
															</>
														)}
													</>
												}
											/>
										</ListItem>
									),
								)}
							</List>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCloseModal}
						disabled={localPaymentProcessing || paymentProcessing}
						sx={{ color: colors.persianRed }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleConfirmPayment}
						disabled={
							localPaymentProcessing ||
							paymentProcessing ||
							!stripe ||
							paymentSuccess
						}
						startIcon={
							localPaymentProcessing || paymentProcessing ?
								<CircularProgress size={20} color="inherit" />
								: <PaymentIcon />
						}
						sx={{
							backgroundColor: "#dc2626",
							"&:hover": {
								backgroundColor: "#b91c1c",
							},
						}}>
						{localPaymentProcessing || paymentProcessing ?
							"Processing..."
							: paymentSuccess ?
								"✓ Payment Successful"
								: feeBreakdown ?
									`Pay ${formatCurrencyWithFee(feeBreakdown.totalCharge)}`
									: "Pay Now"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Success Modal */}
			{openSuccessModal && (
				<NotificationModal
					open={openSuccessModal}
					handleClose={() => setOpenSuccessModal(false)}>
					<Grid container spacing={2} direction="column" alignItems="center">
						<Grid item>
							<Avatar
								src="/assets/icons/congrats-icon.gif"
								sx={{ width: 50, height: 50 }}
								alt="Success Icon"
							/>
						</Grid>
						<Grid item>
							<Typography
								variant="h6"
								sx={{
									color: colors.darkGrey,
									textAlign: "center",
									fontWeight: 600,
								}}>
								Payment Successful!
							</Typography>
							<Typography
								variant="body1"
								sx={{
									color: colors.darkGrey,
									textAlign: "center",
									mt: 1,
								}}>
								Your account has been reactivated.
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: "text.secondary",
									textAlign: "center",
									mt: 1,
								}}>
								Redirecting you to login...
							</Typography>
						</Grid>
					</Grid>
				</NotificationModal>
			)}

			{/* Error Alert Snackbar */}
			<Snackbar
				open={alertState.open}
				autoHideDuration={6000}
				onClose={handleCloseAlert}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}>
				<Alert
					onClose={handleCloseAlert}
					variant="filled"
					severity={alertState.type}
					sx={{ width: "100%" }}>
					{alertState.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

export default AccountBanned;
